import React, { useEffect, useState } from 'react';
import { Badge, Box, Button, Checkbox, Flex, Input, Spinner, Text } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowsClockwise } from 'phosphor-react';
import {
  IAutentiqueDocumentoStatus,
  IAutentiqueSignatarioStatus,
  useAutentique,
} from '../../../../hooks/useAutentique';
import { useEstatuto } from '../../../../hooks/useEstatuto';

interface EstatutoAssinaturaStatusProps {
  cardId: number;
  estatutoId: number;
  /**
   * Dispara um sync-autentique automático ao abrir. Deve ser `false` quando o
   * estatuto já está finalizado — o sync reposiciona o card pela coluna derivada
   * do status (Acompanhamento), tirando-o da coluna "Assinado / Finalizado".
   */
  autoSync?: boolean;
}

// Status por assinante padronizado pelo backend: "pending" | "signed" | "cancelled".
const isSigned = (s: IAutentiqueSignatarioStatus): boolean =>
  !!s.dt_assinatura || /signed|assinad|conclu/i.test(s.status || '');

const isCancelled = (s: IAutentiqueSignatarioStatus): boolean =>
  /cancel|rejeit|reject|recus/i.test(s.status || '');

type Situacao = 'assinou' | 'pendente' | 'cancelada';

// Excluído na Autentique: o sync marca o documento, os assinantes ficam como estavam.
const docCancelado = (d: IAutentiqueDocumentoStatus) =>
  d.status === 'cancelled' || d.status === 'substituido' || d.signatarios.some(isCancelled);

const situacao = (d: IAutentiqueDocumentoStatus, s: IAutentiqueSignatarioStatus): Situacao => {
  // 'substituido': refeito por uma correção da equipe; o novo chega em seguida.
  if (d.status === 'cancelled' || d.status === 'substituido' || isCancelled(s)) return 'cancelada';
  return isSigned(s) ? 'assinou' : 'pendente';
};

const COR: Record<Situacao, string> = { assinou: 'green', pendente: 'orange', cancelada: 'red' };
const ROTULO: Record<Situacao, string> = {
  assinou: 'Assinou',
  pendente: 'Pendente',
  cancelada: 'Cancelada',
};

// Nome na Autentique = "<tipo> · <título>": o tipo identifica o documento (como no back).
const tipoDoNome = (nome: string) => nome.split(' · ')[0];

// A Lista de Presença (todos os presentes) fica num bloco à parte.
const LISTA = 'Lista de Presença';

/** Último envio de cada documento (o recusado e reenviado aparece só no novo), na ordem de envio.
 *  Maior id, como no back. */
const ultimoDeCada = (docs: IAutentiqueDocumentoStatus[]): IAutentiqueDocumentoStatus[] =>
  Array.from(
    docs
      .reduce(
        (m, d) => ((m.get(tipoDoNome(d.nome))?.id ?? -1) > d.id ? m : m.set(tipoDoNome(d.nome), d)),
        new Map<string, IAutentiqueDocumentoStatus>(),
      )
      .values(),
  ).sort((a, b) => a.id - b.id);

const todosAssinaram = (d: IAutentiqueDocumentoStatus) =>
  d.signatarios.length > 0 && d.signatarios.every(isSigned);

interface IPessoa {
  nome: string;
  email: string;
  advogado: boolean;
  docs: { tipo: string; situacao: Situacao }[];
}

/** Grupo de assinatura = quem assina o mesmo conjunto de documentos (decisão de 22/10), na ordem
 *  dos documentos. */
const gruposDePessoas = (docs: IAutentiqueDocumentoStatus[]): IPessoa[][] => {
  const pessoas = new Map<string, IPessoa>();
  docs.forEach(d =>
    d.signatarios.forEach(s => {
      const chave = s.email.toLowerCase();
      const p = pessoas.get(chave) ?? { nome: s.nome, email: s.email, advogado: false, docs: [] };
      p.advogado = p.advogado || s.tipo === 'advogado';
      p.docs.push({ tipo: tipoDoNome(d.nome), situacao: situacao(d, s) });
      pessoas.set(chave, p);
    }),
  );
  const grupos = new Map<string, IPessoa[]>();
  pessoas.forEach(p => {
    const chave = p.docs.map(x => x.tipo).join('|');
    grupos.set(chave, [...(grupos.get(chave) ?? []), p]);
  });
  return Array.from(grupos.values());
};

const Selo: React.FC<{ situacao: Situacao; children: React.ReactNode }> = ({
  situacao: sit,
  children,
}) => (
  <Badge
    flexShrink={0}
    colorScheme={COR[sit]}
    borderRadius="full"
    px={2}
    py={0.5}
    fontSize="10px"
    textTransform="none"
    // Título longo (documento antigo, sem o tipo no nome) quebra a linha em vez de vazar.
    whiteSpace="normal"
  >
    {children}
  </Badge>
);

/** Um selo por documento: quantos já assinaram. */
const Resumo: React.FC<{ docs: IAutentiqueDocumentoStatus[] }> = ({ docs }) => (
  <Flex wrap="wrap" gap={1.5} mb={3}>
    {docs.map(d => {
      const total = d.signatarios.length;
      const assinados = d.signatarios.filter(isSigned).length;
      let sit: Situacao = assinados >= total ? 'assinou' : 'pendente';
      if (docCancelado(d)) sit = 'cancelada';
      return (
        <Selo key={d.id} situacao={sit}>
          {tipoDoNome(d.nome)}: {sit === 'cancelada' ? 'cancelado' : `${assinados} de ${total}`}
        </Selo>
      );
    })}
  </Flex>
);

const Grupo: React.FC<{ numero: number; pessoas: IPessoa[] }> = ({ numero, pessoas }) => {
  const tipos = pessoas[0].docs.map(d => d.tipo);
  const quem = pessoas.every(p => p.advogado)
    ? 'Advogado'
    : `${pessoas.length} pessoa${pessoas.length > 1 ? 's' : ''}`;
  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={3}>
      <Text fontSize="xs" fontWeight="700" color="gray.700">
        Grupo {numero} · {quem}
      </Text>
      <Text fontSize="11px" color="gray.500" mt={0.5}>
        Assina: {tipos.join(', ')}
      </Text>
      <Flex direction="column" gap={2} mt={2}>
        {pessoas.map(p => (
          <Box
            key={p.email}
            bg="gray.50"
            borderWidth="1px"
            borderColor="gray.100"
            borderRadius="lg"
            px={3}
            py={2}
          >
            <Flex align="center" justify="space-between" gap={2}>
              <Box minW={0}>
                <Flex align="center" gap={2}>
                  <Text fontSize="xs" fontWeight="700" color="gray.700" noOfLines={1}>
                    {p.nome}
                  </Text>
                  {p.advogado && (
                    <Badge
                      colorScheme="purple"
                      fontSize="9px"
                      borderRadius="full"
                      px={2}
                      textTransform="none"
                    >
                      Advogado
                    </Badge>
                  )}
                </Flex>
                <Text fontSize="11px" color="gray.500" noOfLines={1}>
                  {p.email}
                </Text>
              </Box>
              {tipos.length === 1 && (
                <Selo situacao={p.docs[0].situacao}>{ROTULO[p.docs[0].situacao]}</Selo>
              )}
            </Flex>
            {tipos.length > 1 && (
              <Flex wrap="wrap" gap={1} mt={1.5}>
                {p.docs.map(d => (
                  <Selo key={d.tipo} situacao={d.situacao}>
                    {d.tipo}: {ROTULO[d.situacao]}
                  </Selo>
                ))}
              </Flex>
            )}
          </Box>
        ))}
      </Flex>
    </Box>
  );
};

/** Lista de Presença: muitos assinantes, com busca, "só pendentes" e os pendentes primeiro. */
const ListaDePresenca: React.FC<{ doc: IAutentiqueDocumentoStatus }> = ({ doc }) => {
  const [busca, setBusca] = useState('');
  const [soPendentes, setSoPendentes] = useState(false);
  const linhas = doc.signatarios.map(s => ({ s, sit: situacao(doc, s) }));
  const assinaram = linhas.filter(l => l.sit === 'assinou').length;
  const termo = busca.trim().toLowerCase();
  const visiveis = linhas
    .filter(l => !soPendentes || l.sit !== 'assinou')
    .filter(l => !termo || `${l.s.nome} ${l.s.email}`.toLowerCase().includes(termo))
    .sort((a, b) => Number(a.sit === 'assinou') - Number(b.sit === 'assinou'));
  let resumoCor = assinaram >= linhas.length ? 'green.600' : 'orange.600';
  if (docCancelado(doc)) resumoCor = 'red.600';
  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={3}>
      <Flex align="center" justify="space-between" gap={2} wrap="wrap">
        <Text fontSize="xs" fontWeight="700" color="gray.700">
          {LISTA} · {linhas.length} pessoa{linhas.length > 1 ? 's' : ''}
        </Text>
        <Text fontSize="xs" fontWeight="700" color={resumoCor}>
          {docCancelado(doc)
            ? 'Assinatura cancelada'
            : `${assinaram} de ${linhas.length} assinaram`}
        </Text>
      </Flex>
      <Flex gap={3} mt={2} align="center" wrap="wrap">
        <Input
          size="sm"
          maxW="260px"
          aria-label="Buscar na Lista de Presença"
          placeholder="Buscar nome ou e-mail"
          value={busca}
          onChange={e => setBusca(e.target.value)}
        />
        <Checkbox
          size="sm"
          isChecked={soPendentes}
          onChange={e => setSoPendentes(e.target.checked)}
        >
          Só pendentes
        </Checkbox>
      </Flex>
      <Box mt={2} maxH="320px" overflowY="auto">
        {visiveis.map(({ s, sit }) => (
          <Flex
            key={s.id ?? s.email}
            align="center"
            justify="space-between"
            gap={2}
            py={1.5}
            borderBottomWidth="1px"
            borderColor="gray.100"
          >
            <Box minW={0}>
              <Text fontSize="xs" fontWeight="600" color="gray.700" noOfLines={1}>
                {s.nome}
              </Text>
              <Text fontSize="11px" color="gray.500" noOfLines={1}>
                {s.email}
              </Text>
            </Box>
            <Selo situacao={sit}>{ROTULO[sit]}</Selo>
          </Flex>
        ))}
        {visiveis.length === 0 && (
          <Text fontSize="xs" color="gray.500" py={2}>
            Ninguém encontrado.
          </Text>
        )}
      </Box>
    </Box>
  );
};

export const EstatutoAssinaturaStatus: React.FC<EstatutoAssinaturaStatusProps> = ({
  cardId,
  estatutoId,
  autoSync = true,
}) => {
  const { getStatus } = useAutentique();
  const { syncAutentique } = useEstatuto();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['autentique-status', cardId],
    queryFn: () => getStatus(cardId),
    // Atualiza sozinho (sem depender do WS) enquanto falta assinatura ou documento: o envio e
    // o reenvio de um recusado chegam aos poucos, em segundo plano. A consulta é só no banco.
    refetchInterval: query => {
      const docs = ultimoDeCada(query.state.data?.data ?? []);
      if (!autoSync || (docs.length > 0 && docs.every(todosAssinaram))) return false;
      return 8000;
    },
  });

  // Força a Autentique a re-consultar e atualizar o banco, depois relê o status
  // por assinante já atualizado.
  const syncMutation = useMutation({
    mutationFn: () => syncAutentique(estatutoId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['autentique-status', cardId] });
      queryClient.invalidateQueries({ queryKey: ['estatutos'] });
      queryClient.invalidateQueries({ queryKey: ['kanban-board'] });
      queryClient.invalidateQueries({ queryKey: ['estatuto-dashboard'] });
      if (estatutoId) queryClient.invalidateQueries({ queryKey: ['estatuto', estatutoId] });
    },
  });

  const { mutate: syncMutate } = syncMutation;

  // Ao abrir, força uma sincronização (best-effort) antes de ler o status.
  // Pulado quando já finalizado (autoSync=false) pra não reposicionar o card.
  useEffect(() => {
    if (autoSync) syncMutate();
    // Só no mount deste card.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId]);

  const docs = ultimoDeCada(data?.data ?? []);

  const header = (
    <Flex align="center" justify="space-between" mb={2}>
      <Text fontSize="xs" fontWeight="700" color="gray.500">
        ASSINATURAS
      </Text>
      {autoSync && (
        <Button
          size="xs"
          variant="ghost"
          leftIcon={<ArrowsClockwise size={12} weight="bold" />}
          onClick={() => syncMutate()}
          isLoading={syncMutation.isPending}
        >
          Atualizar
        </Button>
      )}
    </Flex>
  );

  if (isLoading) {
    return (
      <Box>
        {header}
        <Flex align="center" gap={2} py={2}>
          <Spinner size="sm" color="purple.500" />
          <Text fontSize="xs" color="gray.500">
            Carregando assinaturas…
          </Text>
        </Flex>
      </Box>
    );
  }

  if (docs.length === 0) {
    return (
      <Box>
        {header}
        <Text fontSize="xs" color="gray.500" py={1}>
          Nenhum documento de assinatura encontrado ainda.
        </Text>
      </Box>
    );
  }

  const lista = docs.find(d => tipoDoNome(d.nome) === LISTA);
  const grupos = gruposDePessoas(docs.filter(d => d !== lista));

  return (
    <Box>
      {header}
      <Resumo docs={docs} />
      <Flex direction="column" gap={3}>
        {grupos.map((g, i) => (
          <Grupo key={g[0].email} numero={i + 1} pessoas={g} />
        ))}
        {lista && <ListaDePresenca doc={lista} />}
      </Flex>
    </Box>
  );
};
